import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { EMPTY_STATES, MESSAGE_COPY, ROLES, SAFETY_COPY } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { ReportSheet, SafetyMenu } from '@/components/SafetyActions';
import { useBlockMutations, type ReportTarget } from '@/lib/safety';
import { Avatar, Badge, Button, Card, EmptyState, ErrorNote, Spinner, Textarea } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { ChildSummary, DirectMessage, MessageThreadSummary } from '@/types';

/** 쪽지함 — 명세서 §5.18. 스레드는 (원아, 보호자) 한 쌍이다. */
export function MessagesPage() {
  const membership = useAuth((s) => s.membership);
  const isParent = membership?.role === ROLES.PARENT;
  const navigate = useNavigate();

  const threads = useQuery({
    queryKey: ['messages', 'threads'],
    queryFn: () => apiData<MessageThreadSummary[]>('/messages/threads'),
    refetchInterval: 30_000,
  });

  const { data: children } = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
    enabled: isParent,
  });

  const open = useMutation({
    mutationFn: (childId: string) =>
      apiData<{ id: string }>('/messages/threads', { method: 'POST', body: { childId } }),
    onSuccess: (thread) => navigate(`/messages/${thread.id}`),
  });

  // 학부모: 아직 스레드가 없는 자녀도 바로 시작할 수 있게 보여 준다
  const threadChildIds = new Set((threads.data ?? []).map((t) => t.child.id));
  const startable = isParent
    ? (children ?? []).filter((child) => !threadChildIds.has(child.id))
    : [];

  return (
    <Screen>
      <AppBar title="쪽지" />

      <div className="flex flex-col gap-3 p-4">
        {threads.isPending && <Spinner />}
        {threads.isError && (
          <ErrorNote message="쪽지함을 불러오지 못했어요." onRetry={() => void threads.refetch()} />
        )}

        {threads.isSuccess && threads.data.length === 0 && startable.length === 0 && (
          <EmptyState title={EMPTY_STATES.message.title} body={EMPTY_STATES.message.body} />
        )}

        {threads.data?.map((thread) => (
          <Card key={thread.id} className="p-0">
            <Link to={`/messages/${thread.id}`} className="flex items-center gap-3 p-4">
              <Avatar name={isParent ? thread.child.name : thread.parent.name} size={44} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">
                  {isParent
                    ? `${thread.child.name} 선생님`
                    : `${thread.child.name} · ${thread.parent.name}님`}
                </p>
                <p className="truncate text-sm text-ink-sub">{thread.preview ?? '첫 쪽지를 남겨 보세요.'}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-sm text-ink-sub">{formatRelative(thread.lastMessageAt)}</span>
                {thread.unread > 0 && <Badge count={thread.unread} />}
              </div>
            </Link>
          </Card>
        ))}

        {startable.map((child) => (
          <Card key={child.id} className="flex items-center gap-3">
            <Avatar name={child.name} size={44} />
            <p className="min-w-0 flex-1 truncate font-semibold">{child.name} 선생님</p>
            <Button size="sm" variant="outline" loading={open.isPending} onClick={() => open.mutate(child.id)}>
              쪽지 시작
            </Button>
          </Card>
        ))}
      </div>
    </Screen>
  );
}

/** 대화 화면 — 상대 메시지는 왼쪽, 내 메시지는 오른쪽 */
export function MessageThreadPage() {
  const { id = '' } = useParams();
  const user = useAuth((s) => s.user);
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const { unblock } = useBlockMutations();

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['messages', 'thread', id],
    queryFn: () =>
      apiData<{
        thread: { id: string; child: { name: string } };
        messages: DirectMessage[];
        /** 안전 도구 (§5.30) — 차단 상태와 보내기 가능 여부 */
        safety: {
          counterpart: { id: string; name: string } | null;
          blockedByMe: boolean;
          blockedByOther: boolean;
          canSend: boolean;
          blockedUserIds: string[];
        };
      }>(`/messages/threads/${id}`),
    refetchInterval: 15_000,
    enabled: Boolean(id),
  });

  const send = useMutation({
    mutationFn: () =>
      apiData<DirectMessage>(`/messages/threads/${id}`, {
        method: 'POST',
        body: { content: content.trim() },
      }),
    onSuccess: (message) => {
      setContent('');
      setNotice(message.autoNotice ?? null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['messages', 'thread', id] });
      void queryClient.invalidateQueries({ queryKey: ['messages', 'threads'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '보내지 못했어요.'),
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [data?.messages.length]);

  if (isPending) return <Spinner />;
  if (isError || !data) {
    return (
      <Screen withNav={false}>
        <AppBar title="쪽지" back />
        <ErrorNote message="쪽지를 불러오지 못했어요." onRetry={() => void refetch()} />
      </Screen>
    );
  }

  // ⋯ 메뉴의 상대 — 교직원에게는 보호자, 학부모에게는 마지막으로 답한 선생님
  const lastOther = [...data.messages].reverse().find((message) => message.senderUserId !== user?.id);
  const headerUser = data.safety.counterpart ?? lastOther?.sender ?? null;
  const canSend = data.safety.canSend;

  return (
    // 채팅은 fixed 입력창 대신 앱 셸 구조로 — iOS 주소창 변화에도 입력창이 바닥에 붙는다
    <div className="app-shell mx-auto flex w-full max-w-screen-sm flex-col">
      <AppBar
        title={`${data.thread.child.name} 쪽지함`}
        back
        right={headerUser ? <SafetyMenu target={{ type: 'USER', id: headerUser.id }} user={headerUser} /> : undefined}
      />

      {data.safety.blockedByMe && data.safety.counterpart && (
        <div className="flex items-center gap-2 bg-accent-soft px-4 py-2 text-sm">
          <span className="flex-1">{SAFETY_COPY.blockedBanner}</span>
          <Button size="sm" variant="outline" loading={unblock.isPending} onClick={() => unblock.mutate(data.safety.counterpart!.id)}>
            {SAFETY_COPY.unblock}
          </Button>
        </div>
      )}
      {!data.safety.blockedByMe && data.safety.blockedByOther && (
        <p className="bg-accent-soft px-4 py-2 text-sm">{SAFETY_COPY.blockedByOther}</p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-2 p-4">
          {data.messages.length === 0 && (
            <p className="py-6 text-center text-sm text-ink-sub">{EMPTY_STATES.message.body}</p>
          )}

          {data.messages.map((message) => {
            const mine = message.senderUserId === user?.id;
            return (
              <div key={message.id} className={mine ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  className={
                    mine
                      ? 'max-w-[80%] rounded-md rounded-br-sm bg-primary-soft px-3.5 py-2.5'
                      : 'max-w-[80%] rounded-md rounded-bl-sm bg-surface px-3.5 py-2.5 shadow-card'
                  }
                >
                  {!mine && <p className="mb-0.5 text-sm font-semibold">{message.sender.name}</p>}
                  <p className="whitespace-pre-wrap text-base">{message.content}</p>
                  <p className="mt-1 flex items-center justify-end gap-2 text-[11px] text-ink-sub">
                    {!mine && (
                      <button
                        type="button"
                        className="underline"
                        onClick={() => setReportTarget({ type: 'MESSAGE', id: message.id })}
                      >
                        {SAFETY_COPY.report}
                      </button>
                    )}
                    <span>{formatRelative(message.createdAt)}</span>
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
      </div>

      <div className="border-t border-line bg-surface p-3 pb-[calc(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+12px)]">
        {notice && (
          <p className="mb-2 rounded-sm bg-info-soft px-3 py-2 text-sm">
            {notice}{' '}
            <button type="button" className="underline" onClick={() => setNotice(null)}>
              닫기
            </button>
          </p>
        )}
        {error && <p className="mb-2 text-sm text-notice">{error}</p>}
        <div className="flex items-end gap-2">
          <Textarea
            rows={1}
            className="max-h-32 flex-1"
            placeholder={MESSAGE_COPY.placeholder}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <Button
            icon={<Send size={16} />}
            disabled={!content.trim() || !canSend}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            보내기
          </Button>
        </div>
      </div>

      {reportTarget && <ReportSheet target={reportTarget} onClose={() => setReportTarget(null)} />}
    </div>
  );
}
